import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ASSET_TYPES } from './assetTypes';
import { useContinuityAssets } from './useContinuityAssets';
import ContinuityAssetCard from './ContinuityAssetCard';
import AddContinuityAssetModal from './AddContinuityAssetModal';

const ContinuityAssetsPanel = ({ isReadOnly = false, onReadOnlyAction, onCountChange }) => {
  const { items, loading, create, update, remove } = useContinuityAssets();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('automobile');

  const grouped = useMemo(() => {
    const m = {};
    ASSET_TYPES.forEach((t) => { m[t.key] = []; });
    items.forEach((i) => { if (m[i.asset_type]) m[i.asset_type].push(i); });
    return m;
  }, [items]);

  // Tell the parent Continuity System panel how many assets are on file so
  // it can paint the count into the sub-tab label without a second fetch.
  useEffect(() => {
    if (onCountChange) onCountChange(items.length);
  }, [items.length, onCountChange]);

  const openAdd = () => { if (isReadOnly) return onReadOnlyAction?.(); setEditing(null); setModalOpen(true); };
  const openEdit = (asset) => { if (isReadOnly) return onReadOnlyAction?.(); setEditing(asset); setModalOpen(true); };
  const doDelete = async (asset) => {
    if (isReadOnly) return onReadOnlyAction?.();
    if (!window.confirm(`Remove "${asset.nickname}"? This cannot be undone.`)) return;
    try { await remove(asset.asset_id); toast.success('Removed.'); }
    catch (err) { toast.error(err.message || 'Could not remove.'); }
  };
  const doSubmit = async (payload) => {
    if (editing) await update(editing.asset_id, payload);
    else await create(payload);
  };

  const activeItems = grouped[activeTab] || [];
  const totalCount = items.length;

  return (
    <div className="space-y-4" data-testid="continuity-assets-panel">
      {/* Contextual welcome — live count from the useContinuityAssets hook,
          so it never drifts from what's actually rendered below. */}
      <div
        className="text-sm text-emerald-900 bg-emerald-50/60 border border-emerald-200 rounded-md px-3 py-2"
        data-testid="continuity-assets-welcome"
      >
        {totalCount > 0
          ? <>Your <strong>{totalCount}</strong> Continuity {totalCount === 1 ? 'Asset is' : 'Assets are'} already here &mdash; add more below.</>
          : <>No Continuity Assets yet &mdash; add your first one below.</>
        }
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-serif font-bold text-slate-900">Continuity Assets</h3>
          <p className="text-sm text-slate-600 max-w-xl mt-1">
            Automobiles, life insurance policies, private jets and yachts — the personal
            records the people you trust will need to find. Sits alongside your Portfolio Investments. Add one, or several in a row.
          </p>
        </div>
        <Button onClick={openAdd} data-testid="ca-add-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
          <Plus className="w-4 h-4 mr-1" />Add Assets
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {ASSET_TYPES.map((t) => {
          const count = grouped[t.key].length;
          const isActive = activeTab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key} type="button" onClick={() => setActiveTab(t.key)}
              data-testid={`ca-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition ${
                isActive ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
              <span className={`text-xs px-1.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-white'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
        </div>
      )}

      {!loading && activeItems.length === 0 && (
        <div className="text-center py-8 border border-dashed border-slate-200 rounded-lg">
          <p className="text-sm text-slate-500">
            No {ASSET_TYPES.find((t) => t.key === activeTab)?.label.toLowerCase()} recorded yet.
          </p>
          <Button variant="link" onClick={openAdd} className="text-emerald-700" data-testid="ca-empty-add-link">
            Add your first one →
          </Button>
        </div>
      )}

      {!loading && activeItems.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {activeItems.map((asset) => (
            <ContinuityAssetCard key={asset.asset_id} asset={asset} onEdit={openEdit} onDelete={doDelete} />
          ))}
        </div>
      )}

      {!loading && totalCount > 0 && (
        <p className="text-xs text-slate-500 pt-2">
          {totalCount} {totalCount === 1 ? 'asset' : 'assets'} recorded across all types.
        </p>
      )}

      <AddContinuityAssetModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={doSubmit}
        editing={editing}
      />
    </div>
  );
};

export default ContinuityAssetsPanel;
